import React, { useState, useRef } from "react";
import MaterialModal from "../Modals/MaterialModal";
import MaterialsAPI from "../../API/materials.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { AgGridReact, AgGridColumn } from "ag-grid-react";
import ActionCellRenderer from "./MaterialTableActionCellRenderer";
import { longDate } from "../../utils/DateHelper";

import PropTypes from "prop-types";
import LoadingOverlay from "../Loaders/LoadingOverlay";

import { Trans, useTranslation } from "react-i18next";
import { FILTER_TYPES, TEXT_FILTER_TYPE } from "../../utils/constants";
import { isEmpty } from "lodash";
import BaseAlertModal from "../Modals/BaseAlertModal";

const MaterialTable = ({
    selectedRow,
    setSelectedRow,
    setSelectedRowName,
    nextStep,
}) => {
    const NUMBER_OF_ROWS = 20;
    const HEADERS = [
        "name",
        "technicalName",
        "description",
        "createdAt",
        "actions",
    ];

    const [gridApi, setGridApi] = useState(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [actionRow, setActionRow] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [handleModal, setHandleModal] = useState(false);
    const [isCreateMaterial, setIsCreateMaterial] = useState(true);

    const [, setFiltersUsed] = useState(false);
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});

    const materialsRef = useRef([]);
    const filtersRef = useRef(null);
    const [isDataAvailable, setIsDataAvailable] = useState(true);

    const { t } = useTranslation();

    const FILTER_OPTIONS = [
        {
            label: t("materialName"),
            name: "name",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
    ];

    const applyFilters = (_filters) => {
        filtersRef.current = _filters;
        // Filter for material name only!
        const colId = "name";
        const filterModel = {
            [colId]: {
                filterType: FILTER_TYPES.text,
                type: TEXT_FILTER_TYPE.contains,
                filter: _filters[colId],
            },
        };

        materialsRef.current = [];
        setFilters({ ..._filters });
        gridApi.setFilterModel(filterModel);
        setFiltersUsed(true);
    };

    const handleDelete = (event, rowIndex) => {
        event.stopPropagation();
        setActionRow(rowIndex);
        setDeleteModalVisible(true);
    };

    const handleEdit = (rowIndex) => {
        setIsCreateMaterial(false);
        setActionRow(rowIndex);
        setHandleModal(true);
    };

    const deleteMaterial = () => {
        return new Promise((resolve, reject) => {
            const materialId = materialsRef.current[actionRow].id;
            MaterialsAPI.deleteMaterial(materialId)
                .then((res) => {
                    if (!isEmpty(res)) {
                        SuccessAlert(t("deleteMaterialSuccessMessage"));
                        materialsRef.current = [];
                        gridApi.setDatasource(dataSource);
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch((err) => {
                    ErrorAlert(`Error! ${err}`);
                    reject();
                })
                .finally(() => setIsLoading(false));
        });
    };

    const editOrCreateMaterial = (isCreating, materialData, rowIndex) => {
        let newMaterialsData = [...materialsRef.current];
        if (isCreating) {
            materialsRef.current = [];
            setIsDataAvailable(true);
            gridApi && gridApi.refreshInfiniteCache();
        } else {
            const material = newMaterialsData[rowIndex];
            material.name = materialData?.name;
            material.description = materialData?.description;

            const rowNode = gridApi.getRowNode(material?.id);
            rowNode.setData(material);

            materialsRef.current = newMaterialsData;
            rowNode.setDataValue("actions", true);
            gridApi.refreshCells({ rowNodes: [rowNode] });
        }
    };

    const openCreateModel = () => {
        setIsCreateMaterial(true);
        setHandleModal(true);
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        params.api.setDatasource(dataSource);
    };

    const onRowSelected = ({ node, rowIndex }) => {
        if (!node?.selected) return;

        setActionRow(rowIndex);
        setSelectedRow(node?.data?.id);
        setSelectedRowName(node?.data?.name);
        node.setDataValue("actions", true);
        gridApi.forEachNode((n, index) => {
            if (rowIndex !== index) {
                n.setDataValue("actions", false);
            }
        });
    };

    const setSelectedRowNode = () => {
        if (selectedRow) {
            const node = gridApi.getRowNode(selectedRow);
            if (node) {
                node.setSelected(true);
                node.setDataValue("actions", true);
            }
        }
    };

    const formatDate = ({ value }) => {
        if (value) {
            return longDate(value);
        }
    };

    const dataSource = {
        getRows: async (params) => {
            const { startRow, endRow } = params;

            const { data, totalCount } = await MaterialsAPI.getMaterials(
                NUMBER_OF_ROWS,
                filtersRef.current,
                startRow / NUMBER_OF_ROWS
            );

            let lastRow = -1;
            if (endRow >= totalCount) {
                lastRow = totalCount;
            }

            const formattedData = data.map((item) => ({
                ...item,
                technicalName: item.technicalName || "--",
                description: item.description || "--",
            }));
            materialsRef.current = [...materialsRef.current, ...formattedData];
            setIsDataAvailable(totalCount > 0);
            setIsLoading(false);
            params.successCallback(formattedData, lastRow);
        },
    };

    const components = {
        loading: (params) => {
            if (params?.value) {
                return params?.value;
            } else {
                return "Loading...";
            }
        },
        actionCellRenderer: ActionCellRenderer,
    };

    // const COLUMNS = [
    //     {
    //         headerName: "Name",
    //         field: "name",
    //         filter: true,
    //         minWidth: 150,
    //          cellRenderer: "loading"
    //     },
    //     {
    //         headerName: "Description",
    //         field: "description",
    //         minWidth: 150,
    //     },
    //     {
    //         headerName: "Created At",
    //         field: "name",
    //         valueFormatter: formatDate,
    //         minWidth: 150,
    //     },
    //     {
    //         headerName: "Actions",
    //         field: "name",
    //         cellRenderer: "actionCellRenderer",
    //         cellRendererParams: { handleEdit, handleDelete },
    //         minWidth: 150,
    //     },
    // ];

    return (
        <>
            <div className="steps-btn flex items-center justify-end mb-4">
                <FilterDropdown
                    filterOptions={FILTER_OPTIONS}
                    filters={filters}
                    setFilters={setFilters}
                    applyFilters={applyFilters}
                    component="material-table"
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    disabled={!isDataAvailable}
                />

                <button
                    onClick={openCreateModel}
                    className="default-orange-outline-btn modal-btn-open"
                    data-modal-window="create-material-modal"
                    id="new-material-btn"
                >
                    <i className="fas fa-plus mr-2" />
                    <Trans i18nKey="addNewMaterial">New Material</Trans>
                </button>
            </div>
            <LoadingOverlay active={isLoading}>
                <div className="card-table">
                    <div className="shadow mb-5 pt-4 scan-table table-grid ag-grid-height">
                        <div
                            style={{ height: "100%" }}
                            className="ag-theme-alpine text-center"
                        >
                            <AgGridReact
                                defaultColDef={{
                                    flex: 1,
                                    minWidth: 100,
                                }}
                                getRowNodeId={(data) => data.id}
                                frameworkComponents={components}
                                rowModelType="infinite"
                                rowSelection="single"
                                // cacheOverflowSize={2}
                                // maxConcurrentDatasourceRequests={1}
                                infiniteInitialRowCount={NUMBER_OF_ROWS}
                                // maxBlocksInCache={30}
                                cacheBlockSize={NUMBER_OF_ROWS}
                                onGridReady={onGridReady}
                                onRowSelected={onRowSelected}
                                onModelUpdated={setSelectedRowNode}
                                onRowDoubleClicked={nextStep}
                            >
                                {HEADERS.map((header, index) => {
                                    return (
                                        <AgGridColumn
                                            key={index}
                                            headerName={t(header)}
                                            field={header}
                                            minWidth={150}
                                            {...(header === "actions" && {
                                                cellRenderer:
                                                    "actionCellRenderer",
                                            })}
                                            cellRendererParams={{
                                                handleEdit,
                                                handleDelete,
                                            }}
                                            {...(header === "createdAt" && {
                                                valueFormatter: formatDate,
                                            })}
                                            {...(header === "name" && {
                                                filter: true,
                                                cellRenderer: "loading",
                                            })}
                                        />
                                    );
                                })}
                            </AgGridReact>
                        </div>

                        <BaseAlertModal
                            isVisible={deleteModalVisible}
                            setIsVisible={setDeleteModalVisible}
                            title={t("deleteMaterial")}
                            message={t("deleteMaterialMessage", {
                                toBeDeleted: materialsRef.current[actionRow]
                                    ? materialsRef.current[actionRow].name
                                    : "material",
                            })}
                            action={{ submitAction: deleteMaterial }}
                            type="delete"
                        />
                    </div>
                </div>
            </LoadingOverlay>

            <MaterialModal
                modalButton={handleModal}
                modalButtonSetter={setHandleModal}
                completionHandler={editOrCreateMaterial}
                {...(!isCreateMaterial && {
                    data: materialsRef.current[actionRow],
                    index: actionRow,
                })}
            />
        </>
    );
};

MaterialTable.propTypes = {
    selectedRow: PropTypes.string,
    setSelectedRow: PropTypes.func,
};

export default MaterialTable;
